import React, { useState, useRef, useEffect } from 'react';
import { Song, Playlist } from '../../types';
import analyticsService from '../../services/analyticsService';
import { FrequencyThumbnail } from './FrequencyThumbnail';
import { SongMetadataModal } from './SongMetadataModal';

interface MusicLibraryModeProps {
  songs: Song[];
  playlists: Playlist[];
  currentSongId: string | null;
  isPlaying: boolean;
  isPaused: boolean;
  onPlaySong: (songId: string, options?: { context?: 'library' | 'playlist' | 'queue'; playlistSongs?: Song[]; shuffle?: boolean }) => void;
  onStopSong: () => void;
  onDeleteSong: (songId: string) => void;
  onCreatePlaylist: (name: string) => void;
  onDeletePlaylist: (playlistId: string) => void;
  onAddToPlaylist: (songIdOrPlaylistId: string, playlistIdOrSongIds: string | string[]) => void;
  onRemoveFromPlaylist: (songIdOrPlaylistId: string, playlistIdOrSongIds: string | string[]) => void;
  onOpenUploader: () => void;
  onAddToQueue: (songId: string) => void;
  onClearQueue: () => void;
  onShuffleChange: (enabled: boolean) => void;
  onUpdateSongMetadata?: (songId: string, metadata: Partial<Song>) => void;
}

export const MusicLibraryMode: React.FC<MusicLibraryModeProps> = ({
  songs,
  playlists,
  currentSongId,
  isPlaying,
  isPaused,
  onPlaySong,
  onStopSong,
  onDeleteSong,
  onCreatePlaylist,
  onDeletePlaylist,
  onAddToPlaylist,
  onRemoveFromPlaylist,
  onOpenUploader,
  onAddToQueue,
  onClearQueue,
  onShuffleChange,
  onUpdateSongMetadata,
}) => {
  const [isAutoCycle, setIsAutoCycle] = useState(false);
  const [cycleDuration, setCycleDuration] = useState(30);

  const handleToggleAutoCycle = () => {
    setIsAutoCycle(!isAutoCycle);
  };

  const handleCycleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCycleDuration(Number(event.target.value));
  };

  useEffect(() => {
    if (isAutoCycle) {
      const interval = setInterval(() => {
        // Logic to cycle through visualizers
      }, cycleDuration * 1000);
      return () => clearInterval(interval);
    }
  }, [isAutoCycle, cycleDuration]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);
  const [showNewPlaylistInput, setShowNewPlaylistInput] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showAddToPlaylist, setShowAddToPlaylist] = useState<string | null>(null);
  const [songSearch, setSongSearch] = useState('');
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    // Load view mode from localStorage
    const saved = localStorage.getItem('musicLibraryViewMode');
    return (saved === 'grid' || saved === 'list') ? saved : 'list';
  });
  const [selectedSongIds, setSelectedSongIds] = useState<Set<string>>(new Set());
  const [editingMetadataSongId, setEditingMetadataSongId] = useState<string | null>(null);
  const [showBulkMetadataEditor, setShowBulkMetadataEditor] = useState(false);
  const playlistDropdownRef = useRef<HTMLDivElement>(null);
  const bulkPlaylistDropdownRef = useRef<HTMLDivElement>(null);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('musicLibraryViewMode', viewMode);
  }, [viewMode]);

  // Close playlist dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Don't close if clicking on a checkbox or input
      if (target.tagName === 'INPUT') {
        return;
      }
      
      const inPlaylistDropdown = playlistDropdownRef.current && playlistDropdownRef.current.contains(target);
      const inBulkDropdown = bulkPlaylistDropdownRef.current && bulkPlaylistDropdownRef.current.contains(target);
      
      if (!inPlaylistDropdown && !inBulkDropdown) {
        setShowAddToPlaylist(null);
      }
    };

    if (showAddToPlaylist !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAddToPlaylist]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleCreatePlaylist = () => {
    if (newPlaylistName.trim()) {
      onCreatePlaylist(newPlaylistName.trim());
      setNewPlaylistName('');
      setShowNewPlaylistInput(false);
      analyticsService.trackFeatureUse('playlist_create', newPlaylistName);
    }
  };

  const handleDeleteSong = (songId: string, songName: string) => {
    if (confirm(`Delete "${songName}"? This cannot be undone.`)) {
      onDeleteSong(songId);
      analyticsService.trackFeatureUse('song_delete', songName);
    }
  };

  const handleDeletePlaylist = (playlistId: string, playlistName: string) => {
    if (confirm(`Delete playlist "${playlistName}"? Songs will not be deleted.`)) {
      onDeletePlaylist(playlistId);
      if (selectedPlaylistId === playlistId) {
        setSelectedPlaylistId(null);
      }
      analyticsService.trackFeatureUse('playlist_delete', playlistName);
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (displaySongs.every(song => selectedSongIds.has(song.id))) {
      setSelectedSongIds(new Set());
    } else {
      setSelectedSongIds(new Set(displaySongs.map(s => s.id)));
    }
  };

  const toggleSelectSong = (songId: string) => {
    const newSelection = new Set(selectedSongIds);
    if (newSelection.has(songId)) {
      newSelection.delete(songId);
    } else {
      newSelection.add(songId);
    }
    setSelectedSongIds(newSelection);
  };

  const handleBulkDelete = () => {
    if (selectedSongIds.size === 0) return;
    
    const count = selectedSongIds.size;
    if (confirm(`Delete ${count} song${count > 1 ? 's' : ''}? This cannot be undone.`)) {
      selectedSongIds.forEach(songId => onDeleteSong(songId));
      setSelectedSongIds(new Set());
      analyticsService.trackFeatureUse('bulk_delete', `count:${count}`);
    }
  };

  const handleBulkTogglePlaylist = async (playlistId: string, shouldAdd: boolean) => {
    const count = selectedSongIds.size;
    
    const playlist = playlists.find(p => p.id === playlistId);
    if (!playlist) return;
    
    let updatedSongIds: string[];
    if (shouldAdd) {
      // Add all songs in one operation
      const newSongIds = Array.from(selectedSongIds).filter(songId => !playlist.songIds.includes(songId));
      updatedSongIds = [...playlist.songIds, ...newSongIds];
      await onAddToPlaylist(playlistId, updatedSongIds);
      analyticsService.trackFeatureUse('bulk_add_to_playlist', `count:${count}`);
    } else {
      // Remove all songs in one operation
      // Pass the songs to remove, not the final state
      await onRemoveFromPlaylist(playlistId, Array.from(selectedSongIds));
      analyticsService.trackFeatureUse('bulk_remove_from_playlist', `count:${count}`);
    }
  };

  const handleBulkRemoveFromPlaylist = async () => {
    if (!currentPlaylist) return;
    const count = selectedSongIds.size;
    // Batch all removals into a single operation
    await onRemoveFromPlaylist(currentPlaylist.id, Array.from(selectedSongIds));
    setSelectedSongIds(new Set());
    analyticsService.trackFeatureUse('bulk_remove_from_playlist', `count:${count}`);
  };

  const handleBulkMetadataUpdate = (metadata: Partial<Song>) => {
    if (!onUpdateSongMetadata) return;
    selectedSongIds.forEach(songId => {
      onUpdateSongMetadata(songId, metadata);
    });
    setSelectedSongIds(new Set());
    setShowBulkMetadataEditor(false);
    analyticsService.trackFeatureUse('bulk_metadata_update', `count:${selectedSongIds.size}`);
  };

  const handleBulkAddToQueue = () => {
    selectedSongIds.forEach(songId => onAddToQueue(songId));
    setSelectedSongIds(new Set());
    analyticsService.trackFeatureUse('bulk_add_to_queue', `count:${selectedSongIds.size}`);
  };

  const currentPlaylist = selectedPlaylistId 
    ? playlists.find(p => p.id === selectedPlaylistId) 
    : null;

  const displaySongs = currentPlaylist
    ? songs.filter(song => currentPlaylist.songIds.includes(song.id))
    : songs;

  const hasSelection = selectedSongIds.size > 0;
  const isAllSelected = displaySongs.length > 0 && displaySongs.every(s => selectedSongIds.has(s.id));

  // Play all songs in a playlist
  const handlePlayPlaylist = (playlistId: string | null) => {
    const songsToPlay = playlistId
      ? songs.filter(song => playlists.find(p => p.id === playlistId)?.songIds.includes(song.id))
      : songs;
    
    if (songsToPlay.length === 0) return;
    
    // Update UI state
    onShuffleChange(false);
    
    // Play with appropriate context (handlePlaySong will rebuild the queue)
    const context = playlistId ? 'playlist' : 'library';
    onPlaySong(songsToPlay[0].id, { context, playlistSongs: songsToPlay, shuffle: false });
    analyticsService.trackFeatureUse('playlist_play', playlistId || 'all_songs');
  };

  // Shuffle and play all songs in a playlist
  const handleShufflePlaylist = (playlistId: string | null) => {
    const songsToPlay = playlistId
      ? songs.filter(song => playlists.find(p => p.id === playlistId)?.songIds.includes(song.id))
      : songs;
    
    if (songsToPlay.length === 0) return;
    
    // Update UI state
    onShuffleChange(true);
    
    // Play with shuffle enabled - handlePlaySong will shuffle the queue
    const context = playlistId ? 'playlist' : 'library';
    onPlaySong(songsToPlay[0].id, { context, playlistSongs: songsToPlay, shuffle: true });
    analyticsService.trackFeatureUse('playlist_shuffle', playlistId || 'all_songs');
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <svg className="h-8 w-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
              </svg>
              Music Library Manager
            </h1>
            <p className="text-sm text-gray-600 mt-1">Manage your songs, playlists, and uploads</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
                title="List view"
              >
                <svg className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
                title="Grid view"
              >
                <svg className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>
            
            {/* Upload Button */}
            <button
              onClick={onOpenUploader}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors shadow-sm"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Upload Songs
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Playlists */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Playlists</h2>
            
            {/* All Songs Button */}
            <button
              onClick={() => setSelectedPlaylistId(null)}
              className={`w-full text-left px-3 py-2 rounded-lg mb-2 transition-colors ${
                selectedPlaylistId === null
                  ? 'bg-purple-100 text-purple-700 font-medium'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                </svg>
                <span className="text-sm">All Songs</span>
                <span className="ml-auto text-xs text-gray-500">{songs.length}</span>
              </div>
            </button>

            {/* New Playlist Button */}
            {!showNewPlaylistInput && (
              <button
                onClick={() => setShowNewPlaylistInput(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Playlist
              </button>
            )}

            {/* New Playlist Input */}
            {showNewPlaylistInput && (
              <div className="mb-2">
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreatePlaylist()}
                  placeholder="Playlist name..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-2"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCreatePlaylist}
                    className="flex-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => {
                      setShowNewPlaylistInput(false);
                      setNewPlaylistName('');
                    }}
                    className="flex-1 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Playlist Search */}
          {playlists.length > 0 && (
            <div className="px-4 pb-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search playlists..."
                  value={playlistSearch}
                  onChange={(e) => setPlaylistSearch(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs rounded text-gray-900 placeholder-gray-500 bg-white border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:border-gray-600"
                />
                {playlistSearch && (
                  <button
                    onClick={() => setPlaylistSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Clear search"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Playlist List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-1">
            {playlists
              .filter(playlist =>
                playlist.name.toLowerCase().includes(playlistSearch.toLowerCase())
              )
              .map(playlist => {
              const songCount = playlist.songIds.length;
              return (
                <div
                  key={playlist.id}
                  className={`group relative rounded-lg transition-colors ${
                    selectedPlaylistId === playlist.id
                      ? 'bg-purple-100'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => setSelectedPlaylistId(playlist.id)}
                    className="w-full text-left px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <span className="text-sm text-gray-900 flex-1 truncate">{playlist.name}</span>
                      <span className="text-xs text-gray-500">{songCount}</span>
                    </div>
                  </button>
                  
                  {/* Action buttons on hover */}
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayPlaylist(playlist.id);
                      }}
                      className="p-1 rounded hover:bg-purple-200 transition-colors"
                      title="Play playlist"
                    >
                      <svg className="h-3.5 w-3.5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShufflePlaylist(playlist.id);
                      }}
                      className="p-1 rounded hover:bg-purple-200 transition-colors"
                      title="Shuffle playlist"
                    >
                      <svg className="h-3.5 w-3.5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePlaylist(playlist.id, playlist.name);
                      }}
                      className="p-1 rounded hover:bg-red-100 transition-colors"
                      title="Delete playlist"
                    >
                      <svg className="h-3.5 w-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content - Songs */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Content Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {currentPlaylist ? currentPlaylist.name : 'All Songs'}
                </h2>
                <p className="text-sm text-gray-600">{displaySongs.filter(s => s.name.toLowerCase().includes(songSearch.toLowerCase())).length} song{displaySongs.filter(s => s.name.toLowerCase().includes(songSearch.toLowerCase())).length !== 1 ? 's' : ''}</p>
              </div>
              
              {/* Play and Shuffle buttons */}
              {displaySongs.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePlayPlaylist(selectedPlaylistId)}
                    title="Play all"
                    className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded transition-colors"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleShufflePlaylist(selectedPlaylistId)}
                    title="Shuffle"
                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            
            {/* Search Input */}
            {displaySongs.length > 0 && (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search songs..."
                  value={songSearch}
                  onChange={(e) => setSongSearch(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm rounded text-gray-900 placeholder-gray-500 bg-white border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:border-gray-600"
                />
                {songSearch && (
                  <button
                    onClick={() => setSongSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Clear search"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Songs List/Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {displaySongs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                <svg className="h-24 w-24 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                <p className="text-lg mb-2">No songs yet</p>
                <button
                  onClick={onOpenUploader}
                  className="mt-2 px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Upload your first song
                </button>
              </div>
            ) : displaySongs.filter(song => song.name.toLowerCase().includes(songSearch.toLowerCase())).length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                <svg className="h-24 w-24 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-lg mb-2">No songs found</p>
                <p className="text-sm">Try a different search term</p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
                {/* Select All Header */}
                <div className="sticky top-0 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-3 py-2 flex items-center gap-2 z-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    title="Select all songs"
                    className="h-4 w-4 text-purple-600 border-gray-300 dark:border-gray-500 rounded focus:ring-purple-500 cursor-pointer"
                  />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Select All ({displaySongs.filter(song => song.name.toLowerCase().includes(songSearch.toLowerCase())).length})
                  </span>
                </div>

                {/* Bulk Actions Toolbar */}
                {hasSelection && (
                  <div className="px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                        {selectedSongIds.size} Songs
                      </span>
                      <button
                        onClick={() => setSelectedSongIds(new Set())}
                        title="Clear selection"
                        className="p-1 hover:bg-purple-200 dark:hover:bg-purple-800 text-purple-600 dark:text-purple-300 rounded transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Edit Metadata */}
                      {onUpdateSongMetadata && (
                        <button
                          onClick={() => setShowBulkMetadataEditor(true)}
                          title="Edit metadata"
                          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded transition-colors"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      {/* Add to Playlist */}
                      {playlists.length > 0 && (
                        <div className="relative" ref={bulkPlaylistDropdownRef}>
                          <button
                            onClick={() => setShowAddToPlaylist('bulk')}
                            title="Add to playlist"
                            className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded transition-colors"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                          {showAddToPlaylist === 'bulk' && (
                            <div 
                              className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-20"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-2 space-y-1">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                  Manage Playlists ({selectedSongIds.size} songs)
                                </div>
                                {playlists.map(playlist => {
                                  // Check if ALL selected songs are in this playlist
                                  const allInPlaylist = Array.from(selectedSongIds).every(songId => 
                                    playlist.songIds.includes(songId)
                                  );
                                  return (
                                    <label
                                      key={playlist.id}
                                      className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={allInPlaylist}
                                        onChange={() => {
                                          handleBulkTogglePlaylist(playlist.id, !allInPlaylist);
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="text-gray-700 dark:text-gray-200 flex-1">{playlist.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Remove from Playlist (only when viewing a playlist) */}
                      {currentPlaylist && (
                        <button
                          onClick={handleBulkRemoveFromPlaylist}
                          title="Remove from playlist"
                          className="p-2 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400 rounded transition-colors"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                      )}

                      {/* Add to Queue */}
                      <button
                        onClick={handleBulkAddToQueue}
                        title="Add to queue"
                        className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded transition-colors"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                        </svg>
                      </button>

                      {/* Delete */}
                      <button
                        onClick={handleBulkDelete}
                        title="Delete"
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {displaySongs
                  .filter(song => song.name.toLowerCase().includes(songSearch.toLowerCase()))
                  .map(song => {
                  const isCurrent = song.id === currentSongId;
                  const isActivelyPlaying = isCurrent && isPlaying && !isPaused;
                  return (
                    <div
                      key={song.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        isCurrent ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedSongIds.has(song.id)}
                          onChange={() => toggleSelectSong(song.id)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        {/* Play/Stop Button */}
                        <button
                          onClick={() => {
                            if (isActivelyPlaying) {
                              onStopSong();
                            } else {
                              const context = selectedPlaylistId ? 'playlist' : 'library';
                              const playlistSongs = selectedPlaylistId
                                ? songs.filter(s => playlists.find(p => p.id === selectedPlaylistId)?.songIds.includes(s.id))
                                : songs;
                              onPlaySong(song.id, { context, playlistSongs });
                            }
                          }}
                          className={`p-3 rounded-full transition-colors flex-shrink-0 ${
                            isActivelyPlaying
                              ? 'bg-red-500 hover:bg-red-600 text-white'
                              : 'bg-purple-600 hover:bg-purple-700 text-white'
                          }`}
                          title={isActivelyPlaying ? 'Stop' : 'Play'}
                        >
                          {isActivelyPlaying ? (
                            <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>

                        {/* Song Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-base font-medium text-gray-900 truncate">
                            {song.name}
                          </div>
                          <div className="text-sm text-gray-600 truncate">
                            {song.artist?.trim() || 'Unknown Artist'}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
                            <span>{formatDuration(song.duration)}</span>
                            <span>•</span>
                            <span>{formatFileSize(song.size)}</span>
                            {isActivelyPlaying && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-2 text-purple-600">
                                  <div className="flex gap-1">
                                    <div className="w-1 h-3 bg-purple-600 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                    <div className="w-1 h-3 bg-purple-600 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                                    <div className="w-1 h-3 bg-purple-600 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                                  </div>
                                  Now Playing
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Edit Metadata */}
                          {onUpdateSongMetadata && (
                            <button
                              onClick={() => setEditingMetadataSongId(song.id)}
                              className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Edit metadata"
                            >
                              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {/* Add to Playlist */}
                          {playlists.length > 0 && (
                            <div className="relative" ref={playlistDropdownRef}>
                              <button
                                onClick={() => setShowAddToPlaylist(showAddToPlaylist === song.id ? null : song.id)}
                                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                title="Add to playlist"
                              >
                                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                              </button>
                              
                              {showAddToPlaylist === song.id && (
                                <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-10">
                                  <div className="p-2 space-y-1">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Add to Playlist</div>
                                    {playlists.map(playlist => {
                                      const isInPlaylist = playlist.songIds.includes(song.id);
                                      return (
                                        <label
                                          key={playlist.id}
                                          className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isInPlaylist}
                                            onChange={() => {
                                              if (isInPlaylist) {
                                                onRemoveFromPlaylist(song.id, playlist.id);
                                              } else {
                                                onAddToPlaylist(song.id, playlist.id);
                                              }
                                            }}
                                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                          />
                                          <span className="text-gray-700 dark:text-gray-200 flex-1">{playlist.name}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Add to Queue */}
                          <button
                            onClick={() => {
                              onAddToQueue(song.id);
                              analyticsService.trackFeatureUse('song_added_to_queue_from_library', song.name);
                            }}
                            className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Add to queue"
                          >
                            <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                            </svg>
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => handleDeleteSong(song.id, song.name)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete song"
                          >
                            <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Grid View */
              <>
              {/* Select All Checkbox for Grid */}
              <div className="mb-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select All ({displaySongs.filter(song => song.name.toLowerCase().includes(songSearch.toLowerCase())).length})
                </span>
              </div>

              {/* Bulk Actions Toolbar (grid) */}
              {hasSelection && (
                <div className="mb-3 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                      {selectedSongIds.size} Songs
                    </span>
                    <button
                      onClick={() => setSelectedSongIds(new Set())}
                      title="Clear selection"
                      className="p-1 hover:bg-purple-200 dark:hover:bg-purple-800 text-purple-600 dark:text-purple-300 rounded transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Edit Metadata */}
                    {onUpdateSongMetadata && (
                      <button
                        onClick={() => setShowBulkMetadataEditor(true)}
                        title="Edit metadata"
                        className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    {playlists.length > 0 && (
                      <div className="relative" ref={bulkPlaylistDropdownRef}>
                        <button
                          onClick={() => setShowAddToPlaylist('bulk')}
                          title="Add to playlist"
                          className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded transition-colors"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                          {showAddToPlaylist === 'bulk' && (
                            <div 
                              className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-20"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-2 space-y-1">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                  Manage Playlists ({selectedSongIds.size} songs)
                                </div>
                                {playlists.map(playlist => {
                                  // Check if ALL selected songs are in this playlist
                                  const allInPlaylist = Array.from(selectedSongIds).every(songId => 
                                    playlist.songIds.includes(songId)
                                  );
                                  return (
                                    <label
                                      key={playlist.id}
                                      className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={allInPlaylist}
                                        onChange={() => {
                                          handleBulkTogglePlaylist(playlist.id, !allInPlaylist);
                                        }}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="text-gray-700 dark:text-gray-200 flex-1">{playlist.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                    {currentPlaylist && (
                      <button
                        onClick={handleBulkRemoveFromPlaylist}
                        title="Remove from playlist"
                        className="p-2 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400 rounded transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                    )}
                    {/* Add to Queue */}
                    <button
                      onClick={handleBulkAddToQueue}
                      title="Add to queue"
                      className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded transition-colors"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                      </svg>
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      title="Delete"
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded transition-colors"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {displaySongs
                  .filter(song => song.name.toLowerCase().includes(songSearch.toLowerCase()))
                  .map(song => {
                  const isCurrent = song.id === currentSongId;
                  const isActivelyPlaying = isCurrent && isPlaying && !isPaused;
                  return (
                    <div
                      key={song.id}
                      className={`group bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all ${
                        isCurrent ? 'ring-2 ring-purple-500' : ''
                      }`}
                    >
                      {/* Checkbox */}
                      <div className="mb-2">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSongIds.has(song.id)}
                            onChange={() => toggleSelectSong(song.id)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-xs text-gray-600">Select</span>
                        </label>
                      </div>
                      {/* Album Art / Frequency Thumbnail */}
                      <div className="aspect-square bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                        {song.analysis?.spectralProfile ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <FrequencyThumbnail
                              spectralProfile={song.analysis.spectralProfile}
                              width={200}
                              height={200}
                              className="w-full h-full"
                            />
                          </div>
                        ) : (
                          <svg className="h-12 w-12 text-white opacity-70" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                          </svg>
                        )}
                        <button
                          onClick={() => {
                            if (isActivelyPlaying) {
                              onStopSong();
                            } else {
                              const context = selectedPlaylistId ? 'playlist' : 'library';
                              const playlistSongs = selectedPlaylistId
                                ? songs.filter(s => playlists.find(p => p.id === selectedPlaylistId)?.songIds.includes(s.id))
                                : songs;
                              onPlaySong(song.id, { context, playlistSongs });
                            }
                          }}
                          className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg ${
                            isActivelyPlaying ? 'opacity-100' : ''
                          }`}
                        >
                          {isActivelyPlaying ? (
                            <svg className="h-10 w-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="h-10 w-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </div>

                      {/* Song Info */}
                      <div className="mb-3">
                        <div className="text-sm font-medium text-gray-900 truncate" title={song.name}>
                          {song.name}
                        </div>
                        <div className="text-xs text-gray-600 truncate" title={song.artist || 'Unknown Artist'}>
                          {song.artist?.trim() || 'Unknown Artist'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDuration(song.duration)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Edit Metadata */}
                        {onUpdateSongMetadata && (
                          <button
                            onClick={() => setEditingMetadataSongId(song.id)}
                            className="flex-1 p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                            title="Edit metadata"
                          >
                            <svg className="h-4 w-4 text-blue-600 dark:text-blue-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        {/* Add to Playlist */}
                        {playlists.length > 0 && (
                          <div className="relative flex-1" ref={playlistDropdownRef}>
                            <button
                              onClick={() => setShowAddToPlaylist(showAddToPlaylist === song.id ? null : song.id)}
                              className="w-full p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Add to playlist"
                            >
                              <svg className="h-4 w-4 text-gray-600 dark:text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                            </button>
                            
                            {showAddToPlaylist === song.id && (
                              <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-10">
                                <div className="p-2 space-y-1">
                                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Add to Playlist</div>
                                  {playlists.map(playlist => {
                                    const isInPlaylist = playlist.songIds.includes(song.id);
                                    return (
                                      <label
                                        key={playlist.id}
                                        className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isInPlaylist}
                                          onChange={() => {
                                            if (isInPlaylist) {
                                              onRemoveFromPlaylist(song.id, playlist.id);
                                            } else {
                                              onAddToPlaylist(song.id, playlist.id);
                                            }
                                          }}
                                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-gray-700 dark:text-gray-200 flex-1">{playlist.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Add to Queue */}
                        <button
                          onClick={() => {
                            onAddToQueue(song.id);
                            analyticsService.trackFeatureUse('song_added_to_queue_from_library', song.name);
                          }}
                          className="flex-1 p-1.5 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded transition-colors"
                          title="Add to queue"
                        >
                          <svg className="h-4 w-4 text-purple-600 dark:text-purple-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteSong(song.id, song.name)}
                          className="flex-1 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                          title="Delete song"
                        >
                          <svg className="h-4 w-4 text-red-600 dark:text-red-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Single Song Metadata Modal */}
      {editingMetadataSongId && onUpdateSongMetadata && (
        <SongMetadataModal
          song={songs.find(s => s.id === editingMetadataSongId)!}
          isVisible={true}
          onClose={() => setEditingMetadataSongId(null)}
          onSave={(metadata) => {
            onUpdateSongMetadata(editingMetadataSongId, metadata);
            setEditingMetadataSongId(null);
          }}
        />
      )}

      {/* Bulk Metadata Editor Modal */}
      {showBulkMetadataEditor && onUpdateSongMetadata && (
        <BulkMetadataEditor
          songCount={selectedSongIds.size}
          isVisible={true}
          onClose={() => setShowBulkMetadataEditor(false)}
          onSave={handleBulkMetadataUpdate}
        />
      )}
    </div>
  );
};

// Bulk Metadata Editor Component
interface BulkMetadataEditorProps {
  songCount: number;
  isVisible: boolean;
  onClose: () => void;
  onSave: (metadata: Partial<Song>) => void;
}

const BulkMetadataEditor: React.FC<BulkMetadataEditorProps> = ({
  songCount,
  isVisible,
  onClose,
  onSave
}) => {
  const [artist, setArtist] = useState('');
  const [album, setAlbum] = useState('');
  const [albumArtist, setAlbumArtist] = useState('');
  const [genre, setGenre] = useState('');
  const [year, setYear] = useState('');
  const [composer, setComposer] = useState('');
  const [comment, setComment] = useState('');

  const handleSave = () => {
    const metadata: Partial<Song> = {};
    if (artist.trim()) metadata.artist = artist.trim();
    if (album.trim()) metadata.album = album.trim();
    if (albumArtist.trim()) metadata.albumArtist = albumArtist.trim();
    if (genre.trim()) metadata.genre = genre.trim();
    if (year) metadata.year = parseInt(year);
    if (composer.trim()) metadata.composer = composer.trim();
    if (comment.trim()) metadata.comment = comment.trim();

    if (Object.keys(metadata).length > 0) {
      onSave(metadata);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Bulk Edit Metadata ({songCount} songs)
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> Only filled fields will be updated. Empty fields will not change existing values.
            </p>
          </div>

          <div className="space-y-4">
            {/* Artist */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Artist
              </label>
              <input
                type="text"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Leave empty to keep existing values"
              />
            </div>

            {/* Album */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Album
              </label>
              <input
                type="text"
                value={album}
                onChange={(e) => setAlbum(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Leave empty to keep existing values"
              />
            </div>

            {/* Album Artist */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Album Artist
              </label>
              <input
                type="text"
                value={albumArtist}
                onChange={(e) => setAlbumArtist(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Leave empty to keep existing values"
              />
            </div>

            {/* Genre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Genre
              </label>
              <input
                type="text"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Leave empty to keep existing values"
              />
            </div>

            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Year
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Leave empty to keep existing values"
                min="1900"
                max="2100"
              />
            </div>

            {/* Composer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Composer
              </label>
              <input
                type="text"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="Leave empty to keep existing values"
              />
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Comment
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 resize-none"
                placeholder="Leave empty to keep existing values"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Update {songCount} Song{songCount > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};
